import { Box, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRegisterMutation, useLoginWithCredentialsMutation } from '../../../features/auth/api/authApi';
import { setAuthError, setAuthLoading, setAuthSucceeded } from '../../store/authSlice';
import { paths } from '../../routes/paths';
import { setAccessToken, setRefreshToken } from '../../utils/authStorage';
import { userApi } from '../../../features/user/api/userApi';
import AuthForm from './AuthForm';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    authDialogPaperSx,
    authDialogScrollSx,
    authDialogTitleRowSx,
    authTitleSx,
} from './authDialogStyles';
import { parseAuthApiError } from './authErrors';

const AuthDialog = ({ open, onClose, onSuccess, initialTab = 'login' }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [register] = useRegisterMutation();
    const [loginWithCredentials] = useLoginWithCredentialsMutation();
    const [apiError, setApiError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [contentReady, setContentReady] = useState(false);
    const [activeTab, setActiveTab] = useState(initialTab);

    useEffect(() => {
        if (!open) {
            setContentReady(false);
            setApiError(null);
        }
    }, [open]);

    useEffect(() => {
        setActiveTab(initialTab);
    }, [initialTab, open]);

    const handleAuthSuccess = async (result) => {
        setAccessToken(result.access_token);
        setRefreshToken(result.refresh_token);
        dispatch(setAuthSucceeded());

        if (!result.telegram_linked) {
            navigate('/link-telegram', { state: { from: location.state?.from } });
            onClose?.();
            return;
        }

        try {
            await dispatch(userApi.endpoints.getCurrentUser.initiate()).unwrap();
        } catch {
            // user fetch may fail briefly after auth
        }
        if (typeof onSuccess === 'function') {
            onSuccess();
        } else {
            const from = location.state?.from?.pathname
                ? `${location.state.from.pathname}${location.state.from.search || ''}`
                : paths.projects();
            navigate(from);
        }
        onClose?.();
    };

    const handleRegister = async (payload) => {
        setLoading(true);
        setApiError(null);
        dispatch(setAuthLoading());
        try {
            const result = await register(payload).unwrap();
            await handleAuthSuccess(result);
        } catch (err) {
            const parsed = parseAuthApiError(err?.data?.detail, t, 'registration_failed');
            setApiError(parsed);
            dispatch(setAuthError(parsed.message));
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const handleLogin = async (payload) => {
        setLoading(true);
        setApiError(null);
        dispatch(setAuthLoading());
        try {
            const result = await loginWithCredentials(payload).unwrap();
            await handleAuthSuccess(result);
        } catch (err) {
            const parsed = parseAuthApiError(err?.data?.detail, t, 'login_failed');
            setApiError(parsed);
            dispatch(setAuthError(parsed.message));
            throw err;
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            fullWidth
            disableScrollLock
            slotProps={{
                paper: {
                    sx: authDialogPaperSx,
                },
                modal: {
                    disableScrollLock: true,
                },
                transition: {
                    onEntered: () => setContentReady(true),
                },
            }}
            sx={{
                '& .MuiDialog-container': {
                    alignItems: 'center',
                    py: '16px',
                },
            }}
        >
            <Box sx={authDialogScrollSx}>
                {contentReady && (
                    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <Box sx={authDialogTitleRowSx}>
                            <Typography sx={authTitleSx}>
                                {activeTab === 'register' ? t('Регистрация') : t('Авторизация')}
                            </Typography>
                            <IconButton
                                onClick={onClose}
                                aria-label={t('Закрыть')}
                                sx={{ width: 24, height: 24, p: 0, borderRadius: 0, flexShrink: 0, mt: '2px' }}
                            >
                                <CloseIcon sx={{ color: '#B6B9C2', fontSize: 14 }} />
                            </IconButton>
                        </Box>
                        <AuthForm
                            initialTab={initialTab}
                            onLogin={handleLogin}
                            onRegister={handleRegister}
                            loading={loading}
                            apiError={apiError}
                            onClearApiError={() => setApiError(null)}
                            captchaEnabled={contentReady}
                            showTitle={false}
                            onTabChange={setActiveTab}
                        />
                    </Box>
                )}
            </Box>
        </Dialog>
    );
};

export default AuthDialog;
